import { useCallback, useMemo } from "react";
import { formatValue, asNumber } from "../../lib/kernelFormat.js";
import { CanvasSurface, KernelEmpty, resolveCssColor, lerpColor, colorForKey, fillStipplePath } from "./shared.jsx";

export const kind = "sunburst";

// Radial hierarchy. We split each row's path by `separator` (default "/")
// into levels, then aggregate to a tree. Each ring is one level deep;
// arc angles are proportional to summed values below. Inner rings
// inherit a slightly desaturated color from their parent so the
// hierarchy reads visually.
export function Component({ spec, data, fillHeight = false }) {
    const tree = useMemo(() => buildTree(spec, data), [spec, data]);
    const height = Math.max(160, Math.min(420, Number(spec.height) || 260));

    const draw = useCallback((ctx, { width, height: h, root }) => {
        if (!tree) return;
        const cx = width / 2;
        const cy = h / 2;
        const rOuter = Math.min(width, h) / 2 - 6;
        const maxDepth = tree.maxDepth;
        if (maxDepth < 1) return;
        const ringW = rOuter / (maxDepth + 0.4);
        const innerR = ringW * 0.4;
        const ink = resolveCssColor(root, "var(--ink)") || "#fff";
        const bg = resolveCssColor(root, "var(--plot-bg)") || "#1a1a1a";

        // walk and draw
        const drawNode = (node, startAngle, sweep, depth) => {
            if (depth > 0) {
                const r0 = innerR + (depth - 1) * ringW;
                const r1 = innerR + depth * ringW;
                const baseColor = resolveCssColor(root, node.color);
                // Build the arc path once; reuse for both the lerp wash
                // and the stipple overlay (clip-path needs the path
                // active on ctx when fillStipplePath is called).
                const tracePath = () => {
                    ctx.beginPath();
                    ctx.arc(cx, cy, r1, startAngle, startAngle + sweep, false);
                    ctx.arc(cx, cy, r0, startAngle + sweep, startAngle, true);
                    ctx.closePath();
                };
                tracePath();
                ctx.fillStyle = lerpColor(bg, baseColor, Math.max(0.18, 0.5 - (depth - 1) * 0.12));
                ctx.fill();
                // Stipple overlay — density fades with depth so inner
                // rings stay legible against their children.
                const innerBoxR = r1 + 1;
                const bbox = {
                    left: cx - innerBoxR,
                    top: cy - innerBoxR,
                    right: cx + innerBoxR,
                    bottom: cy + innerBoxR,
                };
                tracePath();
                fillStipplePath(ctx, bbox, baseColor, {
                    density: Math.max(0.35, 0.85 - (depth - 1) * 0.15),
                });
                tracePath();
                ctx.strokeStyle = bg;
                ctx.lineWidth = 1;
                ctx.stroke();

                // label if there's room (arc length > ~30px at midR)
                const midR = (r0 + r1) / 2;
                const arcLen = sweep * midR;
                if (arcLen > 30 && (r1 - r0) > 10) {
                    ctx.save();
                    const midAngle = startAngle + sweep / 2;
                    const tx = cx + Math.cos(midAngle) * midR;
                    const ty = cy + Math.sin(midAngle) * midR;
                    ctx.translate(tx, ty);
                    let rot = midAngle;
                    if (rot > Math.PI / 2 && rot < (3 * Math.PI) / 2) rot += Math.PI;
                    ctx.rotate(rot);
                    ctx.fillStyle = ink;
                    ctx.font = "10px ui-monospace, SFMono-Regular, Menlo, monospace";
                    ctx.textAlign = "center";
                    ctx.textBaseline = "middle";
                    const label = truncateToFit(ctx, String(node.name), arcLen - 6);
                    ctx.fillText(label, 0, 0);
                    ctx.restore();
                }
            }

            // children
            const total = node.value || 1;
            let a = startAngle;
            for (const child of node.children) {
                const childSweep = (child.value / total) * sweep;
                drawNode(child, a, childSweep, depth + 1);
                a += childSweep;
            }
        };

        drawNode(tree.root, -Math.PI / 2, Math.PI * 2, 0);

        // center label
        ctx.fillStyle = ink;
        ctx.font = "11px ui-monospace, SFMono-Regular, Menlo, monospace";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        const centerText = spec.format
            ? formatValue(tree.root.value, spec.format)
            : formatValue(tree.root.value, "number");
        ctx.fillText(centerText, cx, cy);
    }, [tree, spec.format]);

    if (!tree || !tree.root.children.length) {
        return <KernelEmpty message={spec.empty || "no rows"} />;
    }

    // When fillHeight is set (activity-surface widget) stretch to fill the
    // parent instead of the spec's clamped pixel height — same flex column
    // pattern the line/stacked charts use. The draw fn already sizes the
    // radius from the live canvas width/height, so it adapts on its own.
    const outerClass = fillHeight
        ? "flex h-full min-h-0 flex-col rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[10px] py-[8px]"
        : "rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[10px] py-[8px]";

    return (
        <div className={outerClass}>
            <CanvasSurface
                draw={draw}
                deps={[tree, spec.format]}
                height={fillHeight ? null : height}
                className={`w-full bg-[color:var(--plot-bg)] rounded-[2px] border border-[color:var(--line-soft)]${fillHeight ? " flex-1 min-h-0" : ""}`}
            />
        </div>
    );
}

function buildTree(spec, data) {
    const arr = Array.isArray(data) ? data : [];
    if (!arr.length) return null;
    const sep = spec.separator || "/";
    const root = { name: "", value: 0, children: [], color: null };

    for (const row of arr) {
        if (!row) continue;
        const path = String(row[spec.pathField] ?? "").split(sep).map((s) => s.trim()).filter(Boolean);
        const v = asNumber(row[spec.valueField]) ?? 0;
        if (!path.length || v <= 0) continue;
        let node = root;
        for (let i = 0; i < path.length; i++) {
            const name = path[i];
            let child = node.children.find((c) => c.name === name);
            if (!child) {
                const color = spec.color && i === 0 ? spec.color : colorForKey(path.slice(0, i + 1).join("/"));
                child = { name, value: 0, children: [], color };
                node.children.push(child);
            }
            child.value += v;
            node = child;
        }
        root.value += v;
    }

    // compute depth
    let maxDepth = 0;
    const walk = (node, d) => {
        if (d > maxDepth) maxDepth = d;
        for (const c of node.children) walk(c, d + 1);
    };
    walk(root, 0);
    // sort children by value desc at each level for nicer reading
    const sortNode = (n) => {
        n.children.sort((a, b) => b.value - a.value);
        for (const c of n.children) sortNode(c);
    };
    sortNode(root);
    return { root, maxDepth };
}

function truncateToFit(ctx, text, maxWidth) {
    if (ctx.measureText(text).width <= maxWidth) return text;
    let lo = 0, hi = text.length;
    while (lo < hi) {
        const mid = (lo + hi + 1) >> 1;
        if (ctx.measureText(text.slice(0, mid) + "…").width <= maxWidth) lo = mid;
        else hi = mid - 1;
    }
    return text.slice(0, lo) + "…";
}
